import { useEffect } from 'react';

interface SEOProps {
  title: string;
  description: string;
  keywords: string;
  canonical?: string;
  ogType?: string;
  schema?: object;
}

export const useSEO = ({
  title,
  description,
  keywords,
  canonical,
  ogType = 'website',
  schema
}: SEOProps) => {
  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    const fullCanonical = canonical ? `${siteUrl}${canonical}` : siteUrl;

    // Set title
    document.title = title;

    // Set or update meta tags
    const setMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      
      element.setAttribute('content', content);
    };

    // Basic meta tags
    setMetaTag('description', description);
    setMetaTag('keywords', keywords);

    // Canonical URL
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', fullCanonical);

    // Open Graph tags
    setMetaTag('og:title', title, true);
    setMetaTag('og:description', description, true);
    setMetaTag('og:type', ogType, true);
    setMetaTag('og:url', fullCanonical, true);

    // Twitter Card
    setMetaTag('twitter:card', 'summary_large_image');
    setMetaTag('twitter:title', title);
    setMetaTag('twitter:description', description);

    // Geo tags
    setMetaTag('geo.position', '34.9833;135.8167');
    setMetaTag('geo.region', 'JP-26');
    setMetaTag('geo.placename', '京都市山科区');

    // Schema.org JSON-LD
    if (schema) {
      let schemaScript = document.querySelector('script[type="application/ld+json"]');
      
      if (!schemaScript) {
        schemaScript = document.createElement('script');
        schemaScript.setAttribute('type', 'application/ld+json');
        document.head.appendChild(schemaScript);
      }
      
      schemaScript.textContent = JSON.stringify(schema);
    }

    // Cleanup function
    return () => {
      // Remove schema script on unmount
      const schemaScript = document.querySelector('script[type="application/ld+json"]');
      if (schemaScript) {
        schemaScript.remove();
      }
    };
  }, [title, description, keywords, canonical, ogType, schema]);
};

// Schema.org generators
export const generateOrganizationSchema = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'Place',
    '@id': `${siteUrl}/#organization`,
    name: '大石神社',
    alternateName: 'Oishi Shrine',
    description: '京都市山科区にある歴史ある神社。御祭神は忠臣蔵で有名な大石内蔵助。',
    url: siteUrl,
    telephone: '+81-75-581-5645',
    email: 'ooishijinja@gmail.com',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '西野山桜ノ馬場町116',
      addressLocality: '山科区',
      addressRegion: '京都市',
      postalCode: '607-8308',
      addressCountry: 'JP'
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 34.9833,
      longitude: 135.8167
    },
    openingHoursSpecification: {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
      opens: '09:00',
      closes: '17:00'
    }
  };
};

export const generateWebPageSchema = (name: string, description: string, url: string) => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name,
    description,
    url: `${siteUrl}${url}`,
    inLanguage: 'ja',
    isPartOf: {
      '@type': 'WebSite',
      '@id': `${siteUrl}/#website`,
      url: siteUrl,
      name: '大石神社',
      publisher: {
        '@id': `${siteUrl}/#organization`
      }
    }
  };
};

export const generateArticleSchema = (title: string, description: string, url: string, datePublished: string) => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: title,
    description,
    url: `${siteUrl}${url}`,
    datePublished,
    dateModified: new Date().toISOString(),
    author: {
      '@type': 'Organization',
      name: '大石神社'
    },
    publisher: {
      '@type': 'Organization',
      name: '大石神社',
      url: siteUrl
    },
    inLanguage: 'ja'
  };
};

export const generateFAQSchema = (faqs: Array<{ question: string; answer: string }>) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer
      }
    }))
  };
};

export const generateEventSchema = (name: string, description: string, startDate: string, location: string) => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'Event',
    name,
    description,
    startDate,
    location: {
      '@type': 'Place',
      name: location,
      address: {
        '@type': 'PostalAddress',
        streetAddress: '西野山桜ノ馬場町116',
        addressLocality: '山科区',
        addressRegion: '京都市',
        postalCode: '607-8308',
        addressCountry: 'JP'
      }
    },
    organizer: {
      '@type': 'Organization',
      name: '大石神社',
      url: siteUrl
    }
  };
};

export const generateProductSchema = (name: string, description: string, price: string, image: string) => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name,
    description,
    image,
    offers: {
      '@type': 'Offer',
      price: price.replace(/[^0-9]/g, ''),
      priceCurrency: 'JPY',
      availability: 'https://schema.org/InStock',
      seller: {
        '@type': 'Organization',
        name: '大石神社',
        url: siteUrl
      }
    }
  };
};
